(function() {
  'use strict';

  const whenVisible = window.theme?.whenVisible || ((element, callback) => callback(element));

  if (customElements.get('shop-by-categories-section')) return;

  class ShopByCategoriesSection extends HTMLElement {
    constructor() {
      super();
      this._swiper = null;
      this._swiperPromise = null;
      this._resizeFrame = null;
      this._handleResize = this._handleResize.bind(this);
    }

    connectedCallback() {
      this._swiperElement = this.querySelector('.shop-by-categories__swiper');
      if (!this._swiperElement) return;

      window.addEventListener('resize', this._handleResize, { passive: true });
      this._visibilityObserver = whenVisible(this, () => this._handleResize());
    }

    disconnectedCallback() {
      this._visibilityObserver?.disconnect?.();
      window.removeEventListener('resize', this._handleResize);

      if (this._resizeFrame) {
        cancelAnimationFrame(this._resizeFrame);
        this._resizeFrame = null;
      }

      this._destroySwiper();
    }

    _handleResize() {
      if (this._resizeFrame) cancelAnimationFrame(this._resizeFrame);

      this._resizeFrame = requestAnimationFrame(() => {
        this._resizeFrame = null;

        if (!this.isConnected || !this._swiperElement) return;

        if (this._shouldStackMobile()) {
          this._destroySwiper();
          return;
        }

        if (this._swiper) {
          this._swiper.update();
          return;
        }

        this._ensureSwiper();
      });
    }

    _shouldStackMobile() {
      const stackMobile = this.dataset.stackMobile === 'true';
      const mobileBreakpoint = parseInt(this.dataset.mobileBreakpoint, 10) || 640;

      return stackMobile && window.innerWidth <= mobileBreakpoint;
    }

    _ensureSwiper() {
      if (this._swiper || this._swiperPromise || typeof window.loadThemeSwiper !== 'function') return;

      this._swiperPromise = window.loadThemeSwiper()
        .then(() => {
          this._swiperPromise = null;

          if (!this.isConnected || this._shouldStackMobile()) return;

          this._createSwiper();
        })
        .catch((error) => {
          this._swiperPromise = null;
          console.warn('Swiper failed to load for ShopByCategories:', error);
        });
    }

    _createSwiper() {
      this._destroySwiper();

      const slidesDesktop = parseFloat(this.dataset.slidesDesktop) || 3;
      const slidesTablet = parseFloat(this.dataset.slidesTablet) || 2;
      const slidesMobile = parseFloat(this.dataset.slidesMobile) || 1;
      const spaceBetween = parseInt(this.dataset.spaceBetween, 10) || 16;
      const spaceBetweenMobile = parseInt(this.dataset.spaceBetweenMobile, 10) || 12;
      const speed = parseInt(this.dataset.speed, 10) || 350;
      const showNavigation = this.dataset.showNavigation === 'true';
      const showDots = this.dataset.showDots === 'true';
      const slideCount = this._swiperElement.querySelectorAll('.swiper-slide').length;
      const loopEnabled = this.dataset.loop === 'true';
      const loop = loopEnabled && slideCount > Math.ceil(slidesDesktop);
      const navPrev = this.querySelector('.shop-by-categories__nav-btn--prev');
      const navNext = this.querySelector('.shop-by-categories__nav-btn--next');
      const paginationEl = this.querySelector('.shop-by-categories__pagination');

      this._swiper = new Swiper(this._swiperElement, {
        slidesPerView: slidesDesktop,
        spaceBetween: spaceBetween,
        speed: speed,
        loop: loop,
        watchOverflow: true,
        navigation: showNavigation && navPrev && navNext
          ? {
              prevEl: navPrev,
              nextEl: navNext
            }
          : false,
        pagination: showDots && paginationEl
          ? {
              el: paginationEl,
              clickable: true
            }
          : false,
        breakpoints: {
          0: {
            slidesPerView: slidesMobile,
            spaceBetween: spaceBetweenMobile
          },
          641: {
            slidesPerView: slidesTablet,
            spaceBetween: spaceBetween
          },
          1025: {
            slidesPerView: slidesDesktop,
            spaceBetween: spaceBetween
          }
        }
      });
    }

    _destroySwiper() {
      if (!this._swiper) return;

      this._swiper.destroy(true, true);
      this._swiper = null;
    }
  }

  customElements.define('shop-by-categories-section', ShopByCategoriesSection);
})();
